import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useMemo } from "react";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

interface Item { date: string; amount: number; }
interface Props {
  profitData: Item[];
  expenseData: Item[];
  goal: number;
  period: 'YTD'|'MTD'|'WTD';
}
const MONTHS = ["J","F","M","A","M","J","J","A","S","O","N","D"];
const getMonthIndex = (d:string) => new Date(d).getMonth();

function getPaceLine({goal, period, labels}:{goal:number;period:Props['period'];labels:string[]}) {
  if(!goal || labels.length<2) return Array(labels.length).fill(goal||0);
  return labels.map((_,i) => Math.round(goal * (i/(labels.length-1))));
}

function formatXLabel(l: string, period: Props["period"]) {
  if(!l)return l;
  if(period==="YTD") {
    let month = new Date(l).toLocaleString('en-US',{month:'short'});
    return month[0].toUpperCase();
  }
  if(period==="MTD") {
    const d = new Date(l);
    if(!isNaN(d.getTime())) return `${d.getMonth()+1}/${d.getDate()}`;
    return l;
  }
  if(period==="WTD") {
    let day = new Date(l).toLocaleString('en-US',{weekday:'short'});
    return day[0].toUpperCase();
  }
  return l;
}

const UnifiedProfitChart = ({ profitData, expenseData, goal, period }: Props) => {
  const { labels, profit, expense } = useMemo(() => {
    if(period==='YTD') {
      let arr = Array(12).fill(0);
      let arrExpense = Array(12).fill(0);
      profitData.forEach(item=>{
        const d = new Date(item.date);
        if(isNaN(d.getTime())) {
          console.warn('Invalid profit date:', item.date);
          return;
        }
        const mi = d.getMonth();
        if (mi>=0 && mi<12) arr[mi] += item.amount;
      });
      expenseData.forEach(item=>{
        const d = new Date(item.date);
        if(isNaN(d.getTime())) {
          console.warn('Invalid expense date:', item.date);
          return;
        }
        const mi = d.getMonth();
        if (mi>=0 && mi<12) arrExpense[mi] += item.amount;
      });
      return { labels: MONTHS, profit: arr, expense: arrExpense };
    }
    if(period==='MTD') {
      const now = new Date();
      const year = now.getFullYear(), month = now.getMonth();
      const days = Array.from({length: now.getDate()}, (_,i) => new Date(year, month, i+1));
      const labels = days.map(d => `${d.getMonth()+1}/${d.getDate()}`);
      let profit = Array(days.length).fill(0), expense = Array(days.length).fill(0);
      profitData.forEach(item=>{
        const d = new Date(item.date).getDate() - 1;
        if(d >= 0 && d < days.length) profit[d] += item.amount;
      });
      expenseData.forEach(item=>{
        const d = new Date(item.date).getDate() - 1;
        if(d >= 0 && d < days.length) expense[d] += item.amount;
      });
      return { labels, profit, expense };
    }
    const now = new Date(), day = now.getDay();
    const monday = new Date(now); monday.setDate(now.getDate() - ((day+6)%7));
    const weekDays = Array.from({length: 7}, (_,i)=>{
      const d = new Date(monday); d.setDate(monday.getDate()+i); return d;
    });
    const labels = weekDays.map(d => d.toLocaleDateString('en-US',{weekday:'short'})[0].toUpperCase());
    let profit = Array(7).fill(0), expense = Array(7).fill(0);
    profitData.forEach(item=>{
      const idx = weekDays.findIndex(d=>d.toDateString()===new Date(item.date).toDateString());
      if(idx!==-1) profit[idx] += item.amount;
    });
    expenseData.forEach(item=>{
      const idx = weekDays.findIndex(d=>d.toDateString()===new Date(item.date).toDateString());
      if(idx!==-1) expense[idx] += item.amount;
    });
    return { labels, profit, expense };
  }, [profitData,expenseData,period]);

  const totalProfit = useMemo(() => {
    return profit.reduce((sum, val) => sum + val, 0);
  }, [profit]);

  const pace = getPaceLine({goal, period, labels});
  const datasets = [
    {
      label: "Profit",
      data: profit,
      borderColor: "#2563eb",
      backgroundColor: "#2563eb10",
      tension: 0.35,
      pointRadius: 4,
      pointHoverRadius: 7,
      pointBackgroundColor: "#fff",
      pointBorderColor: "#2563eb",
      borderWidth: 2.7,
      fill: false,
      order: 1,
    },
    {
      label: "Expenses",
      data: expense,
      borderColor: "#fb7185",
      backgroundColor: "#fb718510",
      tension: 0.35,
      pointRadius: 4,
      pointHoverRadius: 7,
      pointBackgroundColor: "#fff",
      pointBorderColor: "#fb7185",
      borderWidth: 2,
      fill: false,
      order: 2,
    },
    {
      label: "Goal Pace",
      data: pace,
      borderColor: "#fbbf24",
      backgroundColor: "#fbbf2460",
      borderDash: [11,5],
      borderWidth: 2,
      pointRadius: 0,
      fill: false,
      order: 0,
    }
  ];
  const options = {
    responsive: true,
    plugins: {
      legend: { position: 'top', labels:{boxHeight: 16, font:{size:14}} },
      tooltip: { enabled:true },
    },
    scales: {
      x: {
        title: { display: true, text: period==='YTD'?"Months":(period==='MTD'?"Dates":"Weekdays"), font:{size:15,weight:'bold'} },
        grid: {display:true, color:'#ddd'},
        ticks: { color: '#555', font: { size: 15, family: 'Inter, sans-serif', weight: 500 } },
      },
      y: {
        title: { display: true, text: "Amount", font:{size:14} },
        grid: { color: '#f3f3f3' },
        beginAtZero:true,
        ticks: { color:'#444', font:{size:13} },
      },
    },
  };

  // Remove the entire profit box div returning only the chart container
  return (
    <div className="w-full min-h-[340px]">
      <Line data={{labels, datasets}} options={options} />
    </div>
  );
};
export default UnifiedProfitChart;